const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ROME = 'Europe/Rome';
const ROME_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: ROME,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function validDay(value) {
  const match = DAY.exec(String(value ?? ''));
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return String(value);
}

function utcDay(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  const raw = String(value ?? '');
  if (DAY.test(raw)) return validDay(raw);
  const isoDay = /^(\d{4}-\d{2}-\d{2})T/.exec(raw);
  if (isoDay) return validDay(isoDay[1]);
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function romeDay(value) {
  if (!(value instanceof Date) && DAY.test(String(value ?? ''))) return validDay(value);
  const date = value instanceof Date ? value : new Date(String(value ?? ''));
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(ROME_FORMAT.formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function dayDate(day) {
  return new Date(`${day}T00:00:00.000Z`);
}

function addDays(day, amount) {
  const date = dayDate(day);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function addMonths(day, amount) {
  const date = dayDate(day);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + amount);
  return date.toISOString().slice(0, 10);
}

function lastDayOfMonth(day) {
  const date = dayDate(day);
  date.setUTCMonth(date.getUTCMonth() + 1, 0);
  return date.toISOString().slice(0, 10);
}

function cutoff15AfterData(day) {
  const next = addMonths(day, 1);
  return `${next.slice(0, 7)}-15`;
}

/**
 * Period bounds and effective VAT cutoff for one invoice date.
 *
 * The quarterly interpretation follows literal art. 1 c.1 DPR 100/1998:
 * max(period end, 15th after the operation month). Accountant must confirm
 * this interpretation before reuse by #74/#81; Agenzia 14/E 2019 describes a
 * later cutoff for quarterly payers. December never carries back across year.
 */
export function invoiceReceiptPeriod(invoiceDate, period = 'quarter') {
  const day = utcDay(invoiceDate);
  if (!day || !['month', 'quarter'].includes(period)) return null;
  const [year, month] = day.split('-').map(Number);
  const firstMonth = period === 'quarter' ? Math.floor((month - 1) / 3) * 3 + 1 : month;
  const start = `${year}-${String(firstMonth).padStart(2, '0')}-01`;
  const nextStart = addMonths(start, period === 'quarter' ? 3 : 1);
  const end = addDays(nextStart, -1);
  const cutoff15 = cutoff15AfterData(day);
  const cutoff = month === 12
    ? lastDayOfMonth(day)
    : (end > cutoff15 ? end : cutoff15);
  return {
    from: start,
    to: end,
    cutoff,
  };
}

/** One document-date month and its own effective cutoff inside a period. */
export function invoiceReceiptMonthWindows(window, period = 'quarter') {
  const from = validDay(window?.from);
  const to = validDay(window?.to);
  if (!from || !to || from > to || !['month', 'quarter'].includes(period)) return [];

  const months = [];
  for (let monthStart = `${from.slice(0, 7)}-01`; monthStart <= to; monthStart = addMonths(monthStart, 1)) {
    const monthEnd = lastDayOfMonth(monthStart);
    const lower = monthStart < from ? from : monthStart;
    const upper = monthEnd > to ? to : monthEnd;
    months.push({
      from: lower,
      to: upper,
      cutoff: invoiceReceiptPeriod(lower, period).cutoff,
    });
  }
  return months;
}

/**
 * Classify receipt timing. Prior-period deduction needs receipt and recording
 * by cutoff in the same calendar year; December never carries back across 31 December.
 */
export function classifyInvoiceReceipt({
  invoiceDate, receiptDate, recordedDate = null, period = 'quarter', asOf = receiptDate,
} = {}) {
  const invoiceDay = utcDay(invoiceDate);
  const receivedDay = romeDay(receiptDate);
  const recordedDay = recordedDate == null ? null : romeDay(recordedDate);
  const referenceDay = romeDay(asOf);
  const bounds = invoiceReceiptPeriod(invoiceDay, period);
  if (!invoiceDay || !receivedDay || !referenceDay || !bounds) return null;

  const receivedAfterCutoff = receivedDay > bounds.cutoff;
  const recordedByCutoff = recordedDay != null && recordedDay <= bounds.cutoff;
  const periodClosed = referenceDay > bounds.to;
  const carriesBack = !receivedAfterCutoff
    && recordedByCutoff
    && invoiceDay.slice(0, 4) === receivedDay.slice(0, 4)
    && invoiceDay.slice(0, 4) === recordedDay.slice(0, 4);
  return {
    ...bounds,
    periodClosed,
    receivedAfterCutoff,
    recordedByCutoff,
    late: receivedAfterCutoff,
    carriesBack,
    deductionYear: carriesBack ? invoiceDay.slice(0, 4) : (recordedDay || receivedDay).slice(0, 4),
  };
}

/** Latest month or quarter whose effective receipt cutoff has passed. */
export function latestClosedReceiptPeriod({ period = 'quarter', asOf = new Date() } = {}) {
  const referenceDay = romeDay(asOf);
  if (!referenceDay || !['month', 'quarter'].includes(period)) return null;
  const span = period === 'quarter' ? 3 : 1;
  const current = invoiceReceiptPeriod(referenceDay, period);
  const throughPeriodEnd = (start) => {
    const bounds = invoiceReceiptPeriod(start, period);
    return { ...bounds, cutoff: invoiceReceiptPeriod(bounds.to, period).cutoff };
  };
  let previous = throughPeriodEnd(addMonths(current.from, -span));
  if (referenceDay <= previous.cutoff) {
    previous = throughPeriodEnd(addMonths(previous.from, -span));
  }
  return previous;
}

/** Return Europe/Rome instants covering one inclusive calendar day. */
export function romeDayBounds(day) {
  const valid = validDay(day);
  if (!valid) return null;
  const wallTimeAsUtc = (date) => {
    const [year, month, dateOfMonth] = date.split('-').map(Number);
    const target = Date.UTC(year, month - 1, dateOfMonth);
    let candidate = target;
    for (let i = 0; i < 3; i += 1) {
      const parts = Object.fromEntries(ROME_FORMAT.formatToParts(new Date(candidate))
        .map(({ type, value }) => [type, value]));
      const displayedAsUtc = Date.UTC(
        Number(parts.year), Number(parts.month) - 1, Number(parts.day),
        Number(parts.hour), Number(parts.minute), Number(parts.second),
      );
      candidate += target - displayedAsUtc;
    }
    return new Date(candidate);
  };
  const from = wallTimeAsUtc(valid);
  const to = new Date(wallTimeAsUtc(addDays(valid, 1)).getTime() - 1);
  return { from, to };
}

/** Validate inclusive Europe/Rome receipt-date bounds. */
export function parseReceiptDateRange({ from, to } = {}) {
  if (!from && !to) return { from: null, to: null, error: null };
  const lower = from ? validDay(from) : null;
  const upper = to ? validDay(to) : null;
  if ((from && !lower) || (to && !upper)) {
    return { from: null, to: null, error: 'errors.exportPeriodInvalid' };
  }
  if (lower && upper && lower > upper) {
    return { from: null, to: null, error: 'errors.exportPeriodReversed' };
  }
  return {
    from: lower ? romeDayBounds(lower).from : null,
    to: upper ? romeDayBounds(upper).to : null,
    error: null,
  };
}

function italianDay(day) {
  return `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
}

/** Non-blocking warnings for outbound invoice dates. */
export function outboundInvoiceDateWarnings({ invoiceDate, tipoDocumento = 'TD01', today = new Date() } = {}) {
  const data = utcDay(invoiceDate);
  const oggi = romeDay(today);
  if (!data || !oggi) return [];

  const tipo = String(tipoDocumento || 'TD01').toUpperCase();
  if (tipo === 'TD24') {
    const due = cutoff15AfterData(data);
    if (oggi > due) {
      return [{
        code: 'errors.invoiceDataTD24TooLate',
        params: { tipo, data: italianDay(data), scadenza: italianDay(due) },
      }];
    }
    return [];
  }

  if (tipo === 'TD25') {
    const due = lastDayOfMonth(addMonths(data, 1));
    if (oggi > due) {
      return [{
        code: 'errors.invoiceDataTD25TooLate',
        params: { tipo, data: italianDay(data), scadenza: italianDay(due) },
      }];
    }
    return [];
  }

  if (/^TD1[6-9]$/.test(tipo)) {
    const due = cutoff15AfterData(data);
    if (oggi > due) {
      return [{
        code: 'errors.invoiceDataAutofatturaTooLate',
        params: { tipo, data: italianDay(data), scadenza: italianDay(due) },
      }];
    }
    return [];
  }

  if (!['TD01', 'TD02', 'TD03', 'TD05', 'TD06', 'TD26', 'TD27'].includes(tipo)) return [];

  const age = Math.floor((dayDate(oggi).getTime() - dayDate(data).getTime()) / 86_400_000);
  if (age > 12) {
    return [{
      code: 'errors.invoiceDataImmediateTooOld',
      params: { data: italianDay(data), giorni: String(age) },
    }];
  }
  return [];
}
